import React from 'react';
import { useHistory } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  IconButton,
} from '@material-ui/core';
import { Edit, Delete } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from 'components/ConfirmDialog';
import { getVenues, deleteVenue } from 'services/venuesServices';
import PaginationData from 'types/models/PaginationData';
import Venue from 'types/models/Venue';
import AppContext from 'contexts/AppContext';
import EditVenueContext from 'contexts/EditVenueContext';
import UserRoleType from 'types/enums/UserRoleType';
import SearchBar from 'components/SearchBar';
import useDebounce from 'hooks/useDebounce';
import BooleanIcon from 'components/common/BooleanIcon';

export default (): JSX.Element => {
  const { authData, currentPageListNumber, setCurrentPageListNumber } = React.useContext(AppContext);
  const history = useHistory();
  const [paginationVenue, setPaginationVenue] = React.useState<PaginationData<Venue>>();
  const [openDialog, setOpenDialog] = React.useState(false);
  const [venueIdToDelete, setVenueIdToDelete] = React.useState<string | null>(null);
  const [searchVenueText, setSearchVenueText] = React.useState('');
  const { setSelectedVenue } = React.useContext(EditVenueContext);
  const { t } = useTranslation();

  const loadData = React.useCallback(async (page?: number, searchTerm?: string) => {
    const paginationData = await getVenues(page, searchTerm);
    if (paginationData) setPaginationVenue(paginationData);
  }, []);

  const onDeleteVenue = async () => {
    if (venueIdToDelete) {
      await deleteVenue(venueIdToDelete);
      history.go(0);
    }
  };

  const onCloseDialog = () => {
    setVenueIdToDelete('');
  };
  useDebounce(() => {
    if (searchVenueText.trim()) {
      loadData(undefined, searchVenueText);
    } else {
      loadData();
    }
  }, [searchVenueText]);

  React.useEffect(() => {
    if (currentPageListNumber) {
      loadData(currentPageListNumber);
    }
  }, [currentPageListNumber]);

  const renderEditButton = (venue: Venue): JSX.Element => {
    return (
      <IconButton
        color="inherit"
        onClick={() => {
          if (!venue) return;
          setSelectedVenue(venue);
          history.push(`/private/venues/${venue.id}/edit`);
        }}
      >
        <Edit />
      </IconButton>
    );
  };

  const renderContent = (paginationData: Venue[]): JSX.Element => {
    return (
      <>
        {paginationData.map((venue: Venue) => (
          <TableRow key={venue.id}>
            <TableCell component="th" scope="row">
              {venue.name}
            </TableCell>
            <TableCell className="unique-value-cell">{venue.code}</TableCell>
            <TableCell>
              <BooleanIcon value={venue.enabled} />
            </TableCell>
            <TableCell>{renderEditButton(venue)}</TableCell>
            {authData?.user?.role === UserRoleType.Admin && <TableCell>{renderDeleteButton(venue)}</TableCell>}
          </TableRow>
        ))}
      </>
    );
  };

  const renderDeleteButton = (venue: Venue | null): JSX.Element => {
    return (
      <IconButton
        color="inherit"
        onClick={() => {
          if (!venue) return;
          setOpenDialog(true);
          setVenueIdToDelete(venue.id);
        }}
      >
        <Delete />
      </IconButton>
    );
  };

  return (
    <>
      <ConfirmDialog
        open={openDialog && venueIdToDelete !== null}
        setOpen={setOpenDialog}
        onConfirm={onDeleteVenue}
        onCancel={onCloseDialog}
      />
      <SearchBar
        label={t('listVenues-page-search-placeholder')}
        value={searchVenueText}
        onChange={(e) => setSearchVenueText(e.target.value)}
        onClear={() => setSearchVenueText('')}
      />

      {authData?.user.role === UserRoleType.Admin && (
        <Button
          className="btn-50 my-6"
          variant="contained"
          color="primary"
          onClick={() => {
            history.push('/private/venues/create');
          }}
        >
          {t('listVenues-page-create')}
        </Button>
      )}
      <TableContainer className="table-container" component={Paper}>
        <Table>
          <TableHead className="table-header">
            <TableRow>
              <TableCell>{t('listVenues-page-name')}</TableCell>
              <TableCell>{t('listVenues-page-code')}</TableCell>
              <TableCell>{t('listVenues-page-enabled')}</TableCell>
              <TableCell>{t('listVenues-page-edit')}</TableCell>
              {authData?.user?.role === UserRoleType.Admin && <TableCell>{t('listCommerces-page-delete')}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginationVenue?.data && paginationVenue?.data.length > 0 ? (
              renderContent(paginationVenue.data)
            ) : (
              <TableRow>
                <TableCell component="th" scope="row">
                  {t('listVenues-page-noVenues')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {paginationVenue && (
        <TablePagination
          component="div"
          count={paginationVenue.total}
          page={paginationVenue.currentPage - 1}
          onPageChange={(_, page: number) => {
            setCurrentPageListNumber(page + 1);
          }}
          rowsPerPage={paginationVenue.perPage}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) => {
            return `${from}-${to} ${t('pagination-of')} ${count !== -1 ? count : '0'}`;
          }}
        />
      )}
    </>
  );
};
